import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Trash2, Volume2, VolumeX } from 'lucide-react';
import { api } from '@/lib/api';
import { getCoverExamples, isExamplesAdmin, removeCoverExample, type CoverInfo } from '@/lib/quicktools-api';
import { trackEvent } from '@/lib/analytics';
import { LivingCover, type LivingCoverHandle } from './LivingCover';
import { cn } from '@/lib/utils';

// THE LIVING COVER EXAMPLES (owner 2026-10-17): the ones he makes himself and
// picks, "spread out and allowing for the full cover to be displayed and
// appreciated" -- each one big, the whole 9:16 cover, live (hover, stir, the
// bass), never squeezed into a strip of thumbnails. An admin sees a remove
// button on each. Renders nothing while there are none.

export function useCoverExamples() {
  const [examples, setExamples] = useState<CoverInfo[] | null>(null);
  useEffect(() => {
    let live = true;
    void getCoverExamples().then((x) => live && setExamples(x));
    return () => {
      live = false;
    };
  }, []);
  return [examples, setExamples] as const;
}

export function CoverExamples({ examples, onRemoved, className, columns = 3 }: { examples: CoverInfo[]; onRemoved?: (id: string) => void; className?: string; columns?: 2 | 3 }) {
  const [admin, setAdmin] = useState(false);
  useEffect(() => {
    const token = api.getToken();
    if (token) void isExamplesAdmin(token).then(setAdmin);
  }, []);
  if (!examples.length) return null;
  return (
    <ul
      data-testid="cover-examples"
      className={cn('mx-auto grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8', columns === 3 ? 'max-w-6xl lg:grid-cols-3' : 'max-w-3xl', className)}
    >
      {examples.map((ex) => (
        <li key={ex.id} className="mx-auto w-full max-w-[380px]">
          <ExampleCard example={ex} admin={admin} onRemoved={onRemoved} />
        </li>
      ))}
    </ul>
  );
}

function ExampleCard({ example, admin, onRemoved }: { example: CoverInfo; admin: boolean; onRemoved?: (id: string) => void }) {
  const { t } = useTranslation();
  const ref = useRef<LivingCoverHandle>(null);
  const [sound, setSound] = useState(false);
  const toggle = () => {
    const v = ref.current?.video;
    if (!v) return;
    if (sound) {
      v.muted = true;
      setSound(false);
      return;
    }
    // One example plays at a time.
    document.querySelectorAll<HTMLVideoElement>('[data-testid="cover-examples"] video').forEach((o) => {
      if (o !== v) o.muted = true;
    });
    ref.current?.startSound();
    setSound(true);
    trackEvent('living_cover_example_sound', {});
  };
  const remove = async () => {
    const token = api.getToken();
    if (!token || !window.confirm(t('qt.examples.removeConfirm'))) return;
    if (await removeCoverExample(example.id, token)) onRemoved?.(example.id);
  };
  // A cover in another shape (his 4:5 police car, owner 2026-11-01) shows whole, its overlays where he placed them.
  const shaped = example.size && Math.abs(example.size.w / example.size.h - 9 / 16) >= 0.01 ? example.size : null;
  return (
    <figure className="relative">
      <LivingCover
        ref={ref}
        video={example.video_url}
        poster={example.poster_url}
        kinds={example.float || []}
        amount={example.amount}
        opacity={example.opacity}
        spot={example.spot}
        beats={example.beats}
        shakes={example.shakes}
        dark={example.dark}
        frame={shaped}
        label={example.title || t('qt.examples.alt')}
        className={cn('w-full rounded-3xl shadow-[0_30px_70px_-30px_rgba(0,0,0,0.9)]', !shaped && 'aspect-[9/16]')}
        style={shaped ? { aspectRatio: `${shaped.w} / ${shaped.h}` } : undefined}
      />
      {example.song && (
        <button
          type="button"
          onClick={toggle}
          aria-pressed={sound}
          aria-label={sound ? t('qt.examples.soundOff') : t('qt.examples.soundOn')}
          className="absolute bottom-3 right-3 flex h-11 w-11 items-center justify-center rounded-full bg-black/70 text-white ring-1 ring-white/20"
        >
          {sound ? <Volume2 className="h-5 w-5" aria-hidden /> : <VolumeX className="h-5 w-5" aria-hidden />}
        </button>
      )}
      {admin && (
        <button
          type="button"
          onClick={() => void remove()}
          className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-[12.5px] font-semibold text-white ring-1 ring-white/20"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden /> {t('qt.examples.remove')}
        </button>
      )}
      {example.title && <figcaption className="mt-2 text-center text-[14px] font-medium text-muted-foreground">{example.title}</figcaption>}
    </figure>
  );
}

/**
 * THE EXAMPLES AT THE TOP OF THE TOOL (owner 2026-10-18: "how does that catch anyones eyes and draw them to the tool
 * when its located under the actual tool?"): before a picture is picked, the big preview plays the owner's examples
 * one after another, each a whole loop, with dots to pick one. A picked picture takes its place.
 */
/** `slide`: draws each example some other way (the /canvas phones); the badge then sits above it, not on it. */
export function ExampleReel({ examples, className, slide }: { examples: CoverInfo[]; className?: string; slide?: (ex: CoverInfo, hold: (on: boolean) => void) => ReactNode }) {
  const { t } = useTranslation();
  const [i, setI] = useState(0);
  // An example playing its song stays until the visitor moves on.
  const [held, setHeld] = useState(false);
  const n = examples.length;
  useEffect(() => {
    if (n < 2 || held) return undefined;
    const id = window.setTimeout(() => setI((x) => (x + 1) % n), 7500);
    return () => window.clearTimeout(id);
  }, [i, n, held]);
  // Swipe on a phone, drag with a mouse (owner 2026-10-19: "at first i didnt even realize i could do that").
  const swipe = useRef<{ x: number; y: number } | null>(null);
  if (!n) return null;
  const at = i % n;
  const go = (d: number) => {
    setI((x) => (x + d + n) % n);
    trackEvent('living_cover_example_browse', {});
  };
  const ex = examples[at];
  const next = examples[(at + 1) % n];
  const round = 'flex shrink-0 items-center justify-center rounded-full bg-black/70 text-white ring-1 ring-white/20 transition hover:bg-black/75 active:scale-95';
  const arrow = cn('absolute top-1/2 z-10 h-11 w-11 -translate-y-1/2', round);
  // Beside the phones, halfway down, outside the frames (owner 2026-10-20: "up towards the middle where they were
  // except not in the way"): smaller on a phone screen so the phones keep their size.
  const side = cn('h-8 w-8 sm:h-10 sm:w-10', round);
  const dots = (
    <div className={cn('flex flex-wrap justify-center', slide ? 'mt-2 gap-0' : 'mt-1 gap-0.5')}>
      {examples.map((e, j) => (
        <button
          key={e.id}
          type="button"
          onClick={() => setI(j)}
          aria-label={`${t('qt.examples.badge')} ${j + 1}`}
          aria-current={j === at}
          className={cn('flex h-6 items-center justify-center', slide ? 'w-5' : 'w-6')}
        >
          <span className={cn('block h-2 rounded-full transition-all', j === at ? 'w-5 bg-white' : 'w-2 bg-white/45')} />
        </button>
      ))}
    </div>
  );
  const badge = `${t('qt.examples.badge')} ${at + 1}/${n}${ex.title ? ` · ${ex.title}` : ''}`;
  return (
    <div className="relative" data-testid="qt-example-reel">
      {slide && (
        <p className="mb-2 truncate text-center text-[13px] font-semibold text-foreground/90" data-testid="qt-reel-badge">{badge}</p>
      )}
      <div
        className="relative touch-pan-y"
        onPointerDown={(e) => { swipe.current = { x: e.clientX, y: e.clientY }; }}
        onPointerUp={(e) => {
          const s0 = swipe.current;
          swipe.current = null;
          if (!s0 || n < 2) return;
          const dx = e.clientX - s0.x;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(e.clientY - s0.y)) go(dx < 0 ? 1 : -1);
        }}
        onPointerCancel={() => { swipe.current = null; }}
      >
        {slide ? (
          <div className="-mx-3 flex items-center gap-1 sm:mx-0 sm:gap-2">
            {n > 1 && (
              <button type="button" onClick={() => go(-1)} aria-label={t('qt.examples.prev')} className={side} data-testid="qt-reel-prev">
                <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden />
              </button>
            )}
            <div key={ex.id} className={cn('min-w-0 flex-1', className)}>{slide(ex, setHeld)}</div>
            {n > 1 && (
              <button type="button" onClick={() => go(1)} aria-label={t('qt.examples.next')} className={side} data-testid="qt-reel-next">
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden />
              </button>
            )}
          </div>
        ) : (
          <>
            <LivingCover
              key={ex.id}
              video={ex.video_url}
              poster={ex.poster_url}
              kinds={ex.float || []}
              amount={ex.amount}
              opacity={ex.opacity}
              spot={ex.spot}
              beats={ex.beats}
              shakes={ex.shakes}
              dark={ex.dark}
              label={ex.title || t('qt.examples.alt')}
              className={className}
            />
            <span className="pointer-events-none absolute left-1/2 top-3 max-w-[90%] -translate-x-1/2 truncate whitespace-nowrap rounded-full bg-black/70 px-3 py-1 text-[12.5px] font-semibold text-white ring-1 ring-white/15">
              {badge}
            </span>
          </>
        )}
        {n > 1 && !slide && (
          <>
            <button type="button" onClick={() => go(-1)} aria-label={t('qt.examples.prev')} className={cn(arrow, 'left-2')} data-testid="qt-reel-prev">
              <ChevronLeft className="h-6 w-6" aria-hidden />
            </button>
            <button type="button" onClick={() => go(1)} aria-label={t('qt.examples.next')} className={cn(arrow, 'right-2')} data-testid="qt-reel-next">
              <ChevronRight className="h-6 w-6" aria-hidden />
            </button>
          </>
        )}
      </div>
      {next && next.id !== ex.id && <link rel="preload" as="image" href={next.poster_url} />}
      {n > 1 && (
        <>
          {dots}
          <p className="text-center text-[13px] font-semibold text-foreground/85" data-testid="qt-reel-hint">{t('qt.examples.browseHint', { count: n })}</p>
        </>
      )}
    </div>
  );
}
